import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import SelectOption from "components/common/Select/Select";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import {
  apiDeleteRequest,
  apiGetRequest,
  apiPutRequest,
} from "helpers/Requests";
import CircularLoader from "../../../../components/common/CircularLoader/CircularLoader";
import { QUESTION_TYPES } from "Constants/constant";
import { toast } from "react-toastify";
import {USER_ROLE} from "Constants/constant";
import { roleAccess } from "../../../../helpers/RolesPermission";

import {
    URL_ROSTERING_SHIFT_END_QUESTION,
    URL_ROSTERING_SHIFT_END_QUESTION_FOR_WORKER
} from "store/services/URL";

const ShiftEndQuestions = forwardRef((props, ref) => {
  const [questions, setQuestions] = useState([]);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(props.initialEnabled || false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isAssociated, setIsAssociated] = useState(false);

  const [newQuestion, setNewQuestion] = useState({
    label: "",
    type: "radio",
  });

  const [editQuestion, setEditQuestion] = useState({
    label: "",
    type: "radio",
  });

  useImperativeHandle(
    ref,
    () => ({
      getNewQuestions: () => {
        return questions
          .filter((q) => q.isNew)
          .map((q) => ({
            label: q.question,
            type: q.answer_type,
          }));
      },
      resetNewQuestions: () => {
        setQuestions(questions.map((q) => ({ ...q, isNew: false })));
      },
      refreshQuestions: () => {
        fetchQuestions();
      },
      isEnabled: isEnabled,
    }),
    [questions, isEnabled]
  );
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
 const isAdminOrManager = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);
                      const endpoint = isAdminOrManager
                          ? `${URL_ROSTERING_SHIFT_END_QUESTION}`
                          : `${URL_ROSTERING_SHIFT_END_QUESTION_FOR_WORKER}`;
      const response = await apiGetRequest(endpoint);
      if (response?.data?.show_questions) {
        setQuestions(response?.data?.show_questions);
      } else if (response?.data) {
        setQuestions(Array.isArray(response?.data) ? response?.data : []);
      }
    } catch (error) {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (value) => {
    setIsEnabled(value);
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.label.trim()) return;
    const newQ = {
      id: Date.now(),
      question: newQuestion.label,
      answer_type: newQuestion.type,
      isNew: true,
    };
    setQuestions([...questions, newQ]);
    setNewQuestion({ label: "", type: "radio" });
    setIsAddingQuestion(false);
  };

  const getNewQuestions = () => {
    return questions
      .filter((q) => q.isNew)
      .map((q) => ({
        label: q.question,
        type: q.answer_type,
      }));
  };

  useEffect(() => {
    if (window.shiftEndQuestions) {
      window.shiftEndQuestions.getNewQuestions = getNewQuestions;
    }
  }, [questions]);

  const handleDeleteClick = async (question) => {
  setQuestionToDelete(question);
   setIsAssociated(false);
  setDeleteModalOpen(true);

  if (question.isNew) {
    setIsAssociated(false);
    return;
  }

  try {
    const response = await apiDeleteRequest(
      `/sp/shift-end-question/delete/${question.id}`
    );
   if (response?.data?.question_deleted?.is_associated !== undefined) {
      setIsAssociated(response?.data?.question_deleted?.is_associated);
    }
  } catch (error) {
    toast.error(error);
  } finally {
    setLoading(false);
  }
};
  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

     if (questionToDelete.isNew) {
    setQuestions(questions.filter((q) => q.id !== questionToDelete.id));
    setDeleteModalOpen(false);
    setQuestionToDelete(null);
    setIsAssociated(false);
    return;
  }


    setLoading(true);
    try {
      const response = await apiDeleteRequest(
        `/sp/shift-end-question/delete/${questionToDelete.id}?confirm=true`
      );

      if (response?.data) {
        setQuestions(questions.filter((q) => q.id !== questionToDelete.id));
        setDeleteModalOpen(false);
        setQuestionToDelete(null);
      }
    } catch (error) {
        toast.error(error?.message || "Failed to delete question");
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeBadge = (type) => {
    const typeConfig = {
      radio: { label: "Yes/No", className: "bg-blue-100 text-blue-800" },
      text: { label: "Text Answer", className: "bg-green-100 text-green-800" },
      both: { label: "Both", className: "bg-purple-100 text-purple-800" },
    };

    const config = typeConfig[type] || {
      label: type,
      className: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
      >
        {config.label}
      </span>
    );
  };
  const editQuestions = (question) => {
    setEditingId(question.id);
    setEditQuestion({
      label: question.question,
      type: question.answer_type,
    });
  };
  return (
    <div className="mt-2">
      <div className="w-full h-0.5 mx-1 bg-gray-300 my-8" />
      <h5 className="px-2 pb-4 font-bold">Shift End Questions</h5>
      <div className="grid grid-cols-2 gap-x-2 gap-y-6 mb-6">
        <div className="col-span-1 px-2">
          <div className="flex items-center justify-between">
            <label className="text-gray-700 lg:text-sm font-medium mb-1 sm:text-xs">
              Enable Shift End Questions
            </label>
            <SwitchWithIcon
              onChange={handleToggleEnabled}
              checked={isEnabled}
              disabled={false}
            />
          </div>
        </div>
      </div>

        {isEnabled && (
          <>
            <div className="px-2">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-medium text-gray-700">Default Questions</h5>
                {!isAddingQuestion && !loading && (
                  <button
                    onClick={() => setIsAddingQuestion(true)}
                    className="tracking-normal border-2 border-solid py-2 px-4 text-sm font-semibold rounded-lg inline-flex items-center capitalize gap-x-1 background-gradient border-gradient text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <FaPlus size={12} /> Add Question
                  </button>
                )}
              </div>

              {isAddingQuestion && (
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="text"
                    className="did-floating-input"
                    value={newQuestion.label}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, label: e.target.value })
                    }
                    placeholder="Enter new question..."
                  />
                  <div className="w-40">
                    <SelectOption
                      data={QUESTION_TYPES}
                      selectedOption={QUESTION_TYPES.find(
                        (t) => t.value === newQuestion.type
                      )}
                      handleChange={(option) =>
                        setNewQuestion({ ...newQuestion, type: option.value })
                      }
                    />
                  </div>
                  <button
                    onClick={handleAddQuestion}
                    disabled={loading || !newQuestion.label.trim()}
                    className="tracking-normal border-2 border-solid py-2 px-4 text-sm font-semibold rounded-lg inline-flex items-center capitalize gap-x-1 background-gradient border-gradient text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <FaPlus />
                  </button>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center w-full bg-gray-50 lg:col-span-12 xl:col-span-12 min-h-[200px]">
                  <CircularLoader
                    classes="flex justify-center items-center"
                    classloader="loader-l"
                  />
                </div>
              ) : (
               <div className='space-y-8 pr-4  max-h-96 overflow-y-auto scrollbar-thin'
          style={{
            scrollbarColor: '#38528A transparent',
            scrollbarWidth: 'thin',
          }}>
                  <div className="space-y-3">
                    {questions.map((question) => (
                      <div
                        key={question.id}
                        className="bg-gray-50 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          {editingId === question.id ? (
                            <div className="flex items-center gap-3 flex-1">
                              <input
                                type="text"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                value={editQuestion.label}
                                onChange={(e) =>
                                  setEditQuestion({
                                    ...editQuestion,
                                    label: e.target.value,
                                  })
                                }
                              />
                              <div className="w-40">
                                <SelectOption
                                  data={QUESTION_TYPES}
                                  selectedOption={QUESTION_TYPES.find(
                                    (t) => t.value === editQuestion.type
                                  )}
                                  handleChange={(option) =>
                                    setEditQuestion({
                                      ...editQuestion,
                                      type: option.value,
                                    })
                                  }
                                />
                              </div>
                              <button
                                className="text-green-500 hover:text-green-700 transition-colors"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!editQuestion.label.trim()) {
                                    toast.error("Question cannot be empty");
                                    return;
                                  }

                                  setLoading(true);
                                  try {

                                    const editingQuestion = questions.find( (q) => q.id ===  editingId);

                                    if(editingQuestion?.isNew) {

                                         const updatedQuestions = questions.map((q) =>
        q.id === editingId
          ? {
              ...q,
              question: editQuestion.label,
              answer_type: editQuestion.type,
            }
          : q
      );
      setQuestions(updatedQuestions);
      setEditingId(null);
      setEditQuestion({ label: "", type: "radio" });

                                    } else {
                                    const response = await apiPutRequest(
                                      `/sp/shift-end-question/edit/${editingId}`,
                                      {
                                        questions: [
                                          {
                                            label: editQuestion.label,
                                            type: editQuestion.type,
                                          },
                                        ],
                                        answer_type: editQuestion.type,
                                      }
                                    );

                                    if (response?.data) {
                                      const updatedQuestions = questions.map(
                                        (q) =>
                                          q.id === editingId
                                            ? {
                                                ...q,
                                                question: editQuestion.label,
                                                answer_type: editQuestion.type,
                                              }
                                            : q
                                      );
                                      setQuestions(updatedQuestions);
                                      setEditingId(null);
                                      setEditQuestion({
                                        label: "",
                                        type: "radio",
                                      });
                                      }
                                    }
                                  } catch (error) {
                                      toast.error(error?.message ?? "Update failed.");
                                  } finally {
                                    setLoading(false);
                                  }
                                }}
                                title="Save"
                              >
                                <FaSave size={16} />
                              </button>
                              <button
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEditingId(null);
                                  setEditQuestion({ label: "", type: "radio" });
                                }}
                                title="Cancel"
                              >
                                <FaTimes size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex-1">
                              <p className="font-medium text-gray-800 mb-2">
                                {question.question}
                              </p>
                              <div>
                                {getQuestionTypeBadge(question.answer_type)}
                              </div>
                            </div>
                          )}
                          {editingId !== question.id && (
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  editQuestions(question);
                                }}
                                disabled={loading}
                                title="Edit question"
                              >
                                <FaEdit size={16} />
                              </button>
                              <button
                                className="text-red-500 hover:text-red-700 transition-colors"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteClick(question);
                                }}
                                title="Delete question"
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">
                Delete Question
              </h3>
              <div className="mt-2 px-7 py-3">
             {isAssociated && (
  <p className="text-sm text-yellow-600 mt-2">
    Note: This question is associated with one or more shifts.
  </p>
)}

                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this question? This action
                  cannot be undone.
                </p>
                {questionToDelete && (
                  <p className="text-sm font-medium text-gray-700 mt-2">
                    "{questionToDelete.question}"
                  </p>
                )}
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  className={`px-4 py-2 text-gray-800 rounded-md transition-colors ${
                    loading
                      ? "bg-gray-200 cursor-not-allowed"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setQuestionToDelete(null);
                    setIsAssociated(false);
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 text-white rounded-md transition-colors ${
                    loading
                      ? "bg-red-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                  onClick={handleDeleteConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </span>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ShiftEndQuestions;
